import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { LocalDate } from "@js-joda/core";

import { auth } from "../../../../core/auth";
import { env } from "../../../../env";
import useApi from "../../../../shared/hooks/useApi";
import {
  PatientDocumentScheduleId,
  PatientDocumentTypeId,
  PatientId,
  PatientTaskInstanceId,
} from "../../../../shared/schema/schema";

// Must match the query key used in useTasksNewData.ts
export const TASKS_NEW_REGULAR_QUERY_KEY = "patientTaskRegulars";

/**
 * Fetches a signed S3 URL for a scheduled patient document so it can be
 * opened in the browser. Mirrors the existing AngularJS
 * `openPatientTaskInstanceDocument` function at
 * `patientsUpcomingTasksCtrl.js:288-301`.
 *
 * The endpoint is not in the typed OpenAPI schema, so we hit it directly
 * via axios with the user's auth token.
 */
export function useGenerateDocumentPdfUrl() {
  return useMutation({
    mutationFn: async ({
      patientId,
      documentScheduleId,
    }: {
      patientId: PatientId;
      documentScheduleId: PatientDocumentScheduleId;
    }): Promise<string> => {
      const authState = auth.getAuthOrFail();
      const { data } = await axios.get<{ fileUrl: string }>(
        `${env.API_URL}agencies/${authState.data.agency.id}/agency_members/${authState.data.agencyMember.id}/patients/${patientId}/scheduled_documents/${documentScheduleId}/generate_url`,
        { headers: { Authorization: `Token ${authState.tokens.authToken}` } }
      );
      return data.fileUrl;
    },
  });
}

export function useReopenDocumentForSubmission() {
  const { api } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentScheduleId,
      reason,
    }: {
      documentScheduleId: PatientDocumentScheduleId;
      reason: string;
    }) => {
      return api.post("./patient_documents/:scheduledDocId/openSubmission", {
        path: { scheduledDocId: documentScheduleId },
        body: { reason },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [TASKS_NEW_REGULAR_QUERY_KEY] }),
  });
}

export function useMarkDocumentApproved() {
  const { api } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentScheduleId,
    }: {
      documentScheduleId: PatientDocumentScheduleId;
    }) => {
      return api.put("./patient_documents/:patientDocumentId/mark_as_approved", {
        path: { patientDocumentId: documentScheduleId },
        body: { approvedDate: LocalDate.now() },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [TASKS_NEW_REGULAR_QUERY_KEY] }),
  });
}

export function useMarkDocumentSubmitted() {
  const { api } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskInstanceId,
      documentTypeId,
    }: {
      taskInstanceId: PatientTaskInstanceId;
      documentTypeId: PatientDocumentTypeId;
    }) => {
      // Note: this endpoint uses "agency_member" (singular) and "patient_task_instance" (singular)
      // as found in patientsUpcomingTasksCtrl.js markPatientTaskDocumentAsSubmitted
      return api.post(
        "/agencies/:agencyId/agency_member/:agencyMemberId/patient_task_instance/:patientTaskInstanceId/patient_document",
        {
          path: { patientTaskInstanceId: taskInstanceId },
          body: {
            base64: null,
            documentId: documentTypeId,
            documentVersion: null,
            documentTitle: null,
          },
        }
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [TASKS_NEW_REGULAR_QUERY_KEY] }),
  });
}
